import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ customers: [], cases: [], alerts: [], transactions: [] });
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    if (!q || q.length < 2) {
      setResults({ customers: [], cases: [], alerts: [], transactions: [] });
      return;
    }
    const t = setTimeout(async () => {
      const [c, a] = await Promise.all([
        api.get(`/customers?q=${encodeURIComponent(q)}&limit=5`),
        api.get(`/alerts`),
      ]);
      const cases = (await api.get("/cases")).data.cases || [];
      setResults({
        customers: c.data.customers || [],
        alerts: (a.data.alerts || []).filter(x => x.subject_name?.toLowerCase().includes(q.toLowerCase()) || x.id.includes(q)).slice(0, 5),
        cases: cases.filter(x => x.subject?.toLowerCase().includes(q.toLowerCase()) || x.id.includes(q)).slice(0, 5),
        transactions: [],
      });
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => document.getElementById("cmdk-input")?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (path) => { onClose(); nav(path); };

  return (
    <div data-testid="command-palette" className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] bg-navy-900/40" onClick={onClose}>
      <div className="w-[640px] bg-bg-surface border border-line rounded-md animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-line">
          <Search size={15} className="text-ink-subtle" />
          <input
            id="cmdk-input"
            data-testid="cmdk-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customers, alerts, cases, transactions, BVN, NIN, account…"
            className="flex-1 outline-none text-[14px] bg-transparent placeholder:text-ink-subtle"
          />
          <kbd className="px-1.5 py-0.5 bg-bg-subtle rounded text-[10px] font-mono text-ink-subtle">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {q.length < 2 && (
            <div className="px-4 py-6 text-[12px] text-ink-subtle">
              <div className="mb-3 font-medium text-ink">Quick navigate</div>
              <div className="grid grid-cols-2 gap-2 text-[12px]">
                {[
                  ["g d", "Dashboard", "/dashboard"],
                  ["g c", "Customers", "/customers"],
                  ["g t", "Transactions", "/transactions"],
                  ["g a", "Alerts", "/alerts"],
                  ["g s", "Sanctions", "/screening"],
                  ["g r", "Reporting", "/reporting/str"],
                  ["g m", "Models", "/models"],
                  ["g e", "Examination", "/governance/examination-readiness"],
                ].map(([k, l, p]) => (
                  <button key={p} onClick={() => go(p)} className="flex items-center justify-between px-2 py-1.5 hover:bg-bg-hover rounded">
                    <span>{l}</span>
                    <kbd className="px-1.5 py-0.5 bg-bg-subtle text-[10px] font-mono">{k}</kbd>
                  </button>
                ))}
              </div>
            </div>
          )}
          {q.length >= 2 && (
            <>
              {results.customers.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-ink-subtle border-b border-line bg-bg-subtle">Customers</div>
                  {results.customers.map((c) => (
                    <button key={c.id} onClick={() => go(`/customers/${c.id}`)} className="w-full text-left px-4 py-2 hover:bg-bg-hover border-b border-line/50 last:border-0 flex items-center justify-between">
                      <div>
                        <div className="text-[13px] font-medium">{c.name}</div>
                        <div className="text-[11px] text-ink-subtle">{c.id} · {c.kyc_tier} · {c.risk_band}</div>
                      </div>
                      <span className="text-[11px] text-ink-subtle">↵</span>
                    </button>
                  ))}
                </div>
              )}
              {results.alerts.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-ink-subtle border-b border-line bg-bg-subtle">Alerts</div>
                  {results.alerts.map((a) => (
                    <button key={a.id} onClick={() => go(`/alerts/${a.id}`)} className="w-full text-left px-4 py-2 hover:bg-bg-hover border-b border-line/50 last:border-0">
                      <div className="text-[13px] font-medium">{a.id} — {a.subject_name}</div>
                      <div className="text-[11px] text-ink-subtle">{a.rule}</div>
                    </button>
                  ))}
                </div>
              )}
              {results.cases.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-ink-subtle border-b border-line bg-bg-subtle">Cases</div>
                  {results.cases.map((c) => (
                    <button key={c.id} onClick={() => go(`/cases/${c.id}`)} className="w-full text-left px-4 py-2 hover:bg-bg-hover border-b border-line/50 last:border-0">
                      <div className="text-[13px] font-medium">{c.id} — {c.subject}</div>
                      <div className="text-[11px] text-ink-subtle">{c.status} · {c.priority}</div>
                    </button>
                  ))}
                </div>
              )}
              {results.customers.length === 0 && results.alerts.length === 0 && results.cases.length === 0 && (
                <div className="px-4 py-6 text-[12px] text-ink-subtle">No matches for "{q}".</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
